﻿<script setup lang="ts">
import { useAppStore } from "@/stores/app.ts";
import { useRedistributionStore } from "@/stores/redistributionStore.ts";
import type { ColDef, ColGroupDef } from "ag-grid-community";
import { storeToRefs } from "pinia";
import type { PropType } from "vue";
import { computed, defineComponent, h } from "vue";
import AppGrid from "../../components/AppGrid.vue";
import { getWarehouseDisplay } from "../../utils/warehouseDisplay.ts";
import {
  beforeAfterPoQty,
  formatBeforeAfterPoCell,
  isBeforeAfterPoCell,
  type BeforeAfterPoCell,
} from "./beforeAfterPoCell.ts";
import {
  calculateUrgency,
  formatDate,
  stockoutBarColor,
  stockoutBarWidth,
  urgencyLabel,
} from "./resultHelpers.ts";
const { beforeAfterGridRowData, updatedPOs, beforeAfterView } = storeToRefs(
  useRedistributionStore()
);
const { warehouseOptions } = storeToRefs(useAppStore());

const isDetailsView = computed(() => beforeAfterView.value === "details");
const autoSizeColumns = computed(() => !isDetailsView.value);

/* ── Custom header-group renderer ── */

const poHeaderGroup = defineComponent({
  props: {
    params: {
      type: Object as PropType<{
        displayName?: string;
        warehouseLabel?: string;
        poDate?: string;
      }>,
      required: false,
      default: undefined,
    },
  },
  setup(properties_) {
    return () =>
      h("div", { style: "width:100%;text-align:center;line-height:1.35;" }, [
        h(
          "div",
          {
            style:
              "font-size:11px;font-weight:700;color:var(--dashboard-text-muted);letter-spacing:0.35px;text-transform:uppercase;",
          },
          properties_.params?.displayName ?? ""
        ),
        h(
          "div",
          {
            title: properties_.params?.warehouseLabel ?? "",
            style:
              "font-size:10px;font-weight:600;color:var(--dashboard-text-muted);letter-spacing:0.2px;",
          },
          properties_.params?.warehouseLabel ?? ""
        ),
        h(
          "div",
          {
            style:
              "font-size:10px;font-weight:400;color:var(--dashboard-text-muted);letter-spacing:0.35px;",
          },
          properties_.params?.poDate ?? ""
        ),
      ]);
  },
});

function appendMetric(
  parent: HTMLElement,
  label: string,
  valueNode: Node
): void {
  const metric = document.createElement("div");
  metric.className = "ba-metric";
  const labelEl = document.createElement("span");
  labelEl.className = "ba-metric-label";
  labelEl.textContent = label;
  metric.append(labelEl);
  metric.append(valueNode);
  parent.append(metric);
}

function textValue(text: string): HTMLElement {
  const valueEl = document.createElement("span");
  valueEl.className = "ba-metric-value";
  valueEl.textContent = text;
  return valueEl;
}

function renderPoQtyValue(
  poQty: number,
  comparePoQty: number | undefined
): HTMLElement {
  const span = document.createElement("span");
  span.className = "ba-after-value ba-po-qty-value";
  if (comparePoQty !== undefined && poQty !== comparePoQty) {
    const diff = poQty - comparePoQty;
    span.classList.add(
      poQty > comparePoQty ? "ba-after-gain" : "ba-after-loss"
    );
    span.append(String(poQty));
    const diffSpan = document.createElement("span");
    diffSpan.textContent =
      poQty > comparePoQty ? `(+${String(diff)})` : `(${String(diff)})`;
    span.append(diffSpan);
  } else {
    span.classList.add("dashboard-text-secondary");
    span.textContent = String(poQty);
  }
  return span;
}

function renderSummaryAfterCell(
  afterQty: number,
  beforeQty: number
): HTMLElement {
  const span = document.createElement("span");
  span.className = "ba-after-value ba-po-qty-value";
  if (afterQty > beforeQty) {
    span.classList.add("ba-after-gain");
    span.append(String(afterQty));
    const diffSpan = document.createElement("span");
    diffSpan.textContent = `(+${String(afterQty - beforeQty)})`;
    span.append(diffSpan);
  } else if (afterQty < beforeQty) {
    span.classList.add("ba-after-loss");
    span.append(String(afterQty));
    const diffSpan = document.createElement("span");
    diffSpan.textContent = `(${String(afterQty - beforeQty)})`;
    span.append(diffSpan);
  } else {
    span.classList.add("dashboard-text-secondary");
    span.textContent = String(afterQty);
  }
  return span;
}

function renderBeforeAfterMetricsCell(
  value: unknown,
  comparePoQty?: number
): HTMLElement {
  const cell: BeforeAfterPoCell = isBeforeAfterPoCell(value)
    ? value
    : {
        projectedStock: 0,
        poQty: typeof value === "number" ? value : 0,
        total: typeof value === "number" ? value : 0,
        weeksOfStock: 0,
      };
  const wrapper = document.createElement("div");
  wrapper.className = "ba-metrics-cell";
  wrapper.title = formatBeforeAfterPoCell(cell);
  appendMetric(wrapper, "Projected", textValue(String(cell.projectedStock)));
  appendMetric(wrapper, "PO Qty", renderPoQtyValue(cell.poQty, comparePoQty));
  appendMetric(wrapper, "Total", textValue(String(cell.total)));
  appendMetric(wrapper, "WOS", textValue(String(cell.weeksOfStock)));
  return wrapper;
}

const rowData = computed(() => beforeAfterGridRowData.value);

/* ── Column Definitions ── */

const columnDefs = computed<(ColDef | ColGroupDef)[]>(() => {
  const fixed: ColDef[] = [
    {
      field: "sku",
      headerName: "SKU",
      pinned: "left",
      cellRenderer: (parameters: { value: string }) => {
        const strongEl = document.createElement("strong");
        strongEl.className = "ba-fixed-column-content";
        strongEl.textContent = parameters.value;
        return strongEl;
      },
      width: 90,
      cellStyle: { justifyContent: "flex-start" },
      headerClass: "ba-left-header",
    },
    {
      field: "description",
      headerName: "Description",
      pinned: "left",
      width: 110,
      wrapText: true,
      autoHeight: true,
      cellStyle: {
        fontSize: "11px",
        lineHeight: "1.3",
        paddingTop: "4px",
        paddingBottom: "4px",
        paddingLeft: "3px",
        paddingRight: "3px",
        justifyContent: "flex-start",
      },
      headerClass: "ba-left-header",
    },
    {
      field: "daysToStockout",
      headerName: "Days to Stockout",
      pinned: "left",
      width: 150,
      cellStyle: {
        fontSize: "11px",
        paddingTop: "4px",
        paddingBottom: "4px",
        paddingLeft: "3px",
        paddingRight: "3px",
        lineHeight: "1.3",
        justifyContent: "center",
      },
      headerClass: "ba-left-header ba-header-center",
      cellRenderer: (parameters: { value: number }) => {
        const days = parameters.value;
        const wrapper = document.createElement("div");
        wrapper.className = "flex w-full items-center justify-center gap-1.5";
        const track = document.createElement("div");
        track.className = "ba-stockout-track";
        const fill = document.createElement("div");
        fill.className = "ba-stockout-fill";
        fill.setAttribute(
          "style",
          `width:${stockoutBarWidth(days)};background:${stockoutBarColor(days)}`
        );
        track.append(fill);
        const strong = document.createElement("strong");
        strong.style.fontSize = "11px";
        strong.textContent = String(days);
        wrapper.append(track);
        wrapper.append(strong);
        return wrapper;
      },
    },
    {
      headerName: "Urgency",
      pinned: "left",
      width: 105,
      cellStyle: { justifyContent: "flex-start" },
      headerClass: "ba-left-header",
      headerTooltip:
        "From days of cover at arrival: Critical ≤ 3 days, Moderate ≤ 14 days, OK otherwise. N/A when daily rate of sale is 0.",
      valueGetter: (parameters: { data: Record<string, unknown> }) =>
        parameters.data.daysToStockout as number,
      cellRenderer: (parameters: {
        value: number;
        data: Record<string, unknown>;
      }) => {
        const dailyRateOfSale = parameters.data.dailyRateOfSale as number;
        const label = urgencyLabel(
          calculateUrgency(parameters.value, dailyRateOfSale),
          dailyRateOfSale
        );
        const themeByClass: Record<
          string,
          { background: string; color: string }
        > = {
          "urgency-critical": { background: "#fdecea", color: "#c0392b" },
          "urgency-moderate": { background: "#fff3cd", color: "#856404" },
          "urgency-ok": { background: "#eafaf1", color: "#27ae60" },
          "urgency-na": { background: "#f0f0f0", color: "#888888" },
        };
        const theme = themeByClass[label.cssClass];
        const span = document.createElement("span");
        span.className = "ba-urgency-pill ba-fixed-column-content";
        if (theme) {
          span.style.background = theme.background;
          span.style.color = theme.color;
        }
        span.textContent = `${label.emoji} ${label.text}`;
        return span;
      },
    },
  ];

  const poGroups: ColGroupDef[] = updatedPOs.value.map((po) => ({
    headerName: po.purchaseOrderNumber,
    headerGroupComponent: poHeaderGroup,
    headerGroupComponentParams: {
      warehouseLabel: getWarehouseDisplay(
        po.division,
        po.warehouse,
        warehouseOptions.value,
        po.warehouseName
      ),
      poDate: formatDate(po.arrivalDate),
    },
    headerClass: "ba-po-group",
    children: isDetailsView.value
      ? [
          {
            field: `${po.purchaseOrderNumber}_before`,
            headerName: "Before",
            width: 92,
            headerClass: "ba-before-header",
            headerTooltip:
              "Projected warehouse stock at arrival, this PO's quantity, their total, and weeks of stock before redistribution.",
            cellStyle: {
              textAlign: "center",
              borderLeft: "1px solid #dee2e6",
            },
            cellRenderer: (parameters: { value: unknown }) =>
              renderBeforeAfterMetricsCell(parameters.value),
          },
          {
            field: `${po.purchaseOrderNumber}_after`,
            headerName: "After",
            width: 108,
            headerTooltip:
              "Projected warehouse stock at arrival, this PO's quantity, their total, and weeks of stock after redistribution.",
            cellStyle: { textAlign: "center" },
            cellRenderer: (parameters: {
              value: unknown;
              data: Record<string, unknown>;
            }) => {
              const before =
                parameters.data[`${po.purchaseOrderNumber}_before`];
              const comparePoQty = isBeforeAfterPoCell(before)
                ? before.poQty
                : typeof before === "number"
                  ? before
                  : undefined;
              return renderBeforeAfterMetricsCell(
                parameters.value,
                comparePoQty
              );
            },
          },
        ]
      : [
          {
            field: `${po.purchaseOrderNumber}_before`,
            headerName: "Before",
            width: 80,
            headerClass: "ba-before-header",
            headerTooltip: "PO quantity before redistribution.",
            cellStyle: {
              textAlign: "center",
              borderLeft: "1px solid #dee2e6",
            },
            valueGetter: (parameters: { data: Record<string, unknown> }) =>
              beforeAfterPoQty(
                parameters.data[`${po.purchaseOrderNumber}_before`]
              ),
          },
          {
            field: `${po.purchaseOrderNumber}_after`,
            headerName: "After",
            width: 100,
            headerTooltip: "PO quantity after redistribution.",
            cellStyle: { textAlign: "center" },
            valueGetter: (parameters: { data: Record<string, unknown> }) =>
              beforeAfterPoQty(
                parameters.data[`${po.purchaseOrderNumber}_after`]
              ),
            cellRenderer: (parameters: {
              value: number;
              data: Record<string, unknown>;
            }) =>
              renderSummaryAfterCell(
                parameters.value,
                beforeAfterPoQty(
                  parameters.data[`${po.purchaseOrderNumber}_before`]
                )
              ),
          },
        ],
  }));

  const trailing: ColDef[] = [
    {
      field: "totalBefore",
      headerName: "Total Before",
      width: 130,
      headerClass: "ba-before-header",
      cellStyle: { textAlign: "center", borderLeft: "1px solid #dee2e6" },
    },
    {
      field: "totalAfter",
      headerName: "Total After",
      width: 120,
      headerClass: "ba-before-header",
      cellStyle: {
        textAlign: "center",
        fontWeight: "700",
        borderLeft: "1px solid #dee2e6",
      },
    },
    {
      headerName: "✓",
      width: 52,
      headerClass: "ba-before-header",
      valueGetter: (parameters: { data: Record<string, unknown> }) =>
        parameters.data.totalBefore === parameters.data.totalAfter,
      cellRenderer: (parameters: { value: boolean }) =>
        parameters.value ? "✅" : "❌",
      cellStyle: {
        textAlign: "center",
        fontSize: "16px",
        borderLeft: "1px solid #dee2e6",
      },
    },
    {
      headerName: "",
      flex: 1,
      minWidth: 1,
      resizable: false,
      headerClass: "ba-before-header",
      suppressSizeToFit: false,
      cellStyle: {
        borderLeft: "1px solid #dee2e6",
      },
    },
  ];

  return [...fixed, ...poGroups, ...trailing];
});

const defaultColDef: ColDef = {
  sortable: false,
  filter: false,
  resizable: true,
};

function getRowHeight(): number {
  return isDetailsView.value ? 136 : 76;
}

function setView(view: "summary" | "details"): void {
  beforeAfterView.value = view;
}
</script>

<template>
  <div
    class="before-after-grid flex min-h-0 min-w-0 flex-1 flex-col"
    :class="
      isDetailsView ? 'before-after-grid-details' : 'before-after-grid-summary'
    "
  >
    <div class="ba-view-toggle-row">
      <div
        class="dc-activity-toggle"
        role="group"
        aria-label="Before and after view"
      >
        <button
          type="button"
          class="dc-activity-toggle-option"
          :class="{ 'dc-activity-toggle-option--selected': !isDetailsView }"
          data-testid="before-after-summary-toggle"
          @click="setView('summary')"
        >
          Summary
        </button>
        <button
          type="button"
          class="dc-activity-toggle-option"
          :class="{ 'dc-activity-toggle-option--selected': isDetailsView }"
          data-testid="before-after-details-toggle"
          @click="setView('details')"
        >
          Details
        </button>
      </div>
      <p
        v-if="isDetailsView"
        class="dashboard-text-muted m-0 text-xs"
        data-testid="before-after-scroll-hint"
      >
        Scroll horizontally to compare all PO columns.
      </p>
    </div>
    <AppGrid
      id="before-after-grid"
      :key="beforeAfterView"
      class="min-w-0 flex-1"
      :column-defs="columnDefs"
      :row-data="rowData"
      :default-col-def="defaultColDef"
      :get-row-height="getRowHeight"
      :auto-size-columns="autoSizeColumns"
      :fill-container="true"
      show-top-border
    />
  </div>
</template>
